import type { Dayjs } from 'dayjs';

/**
 * 列表筛选的公共规则。四个列表页共用，保证同一种筛选在哪一页上都是同一个行为。
 */

/**
 * 筛选用的金额边界格式。与后端 money.ts 的 `normalizeAmountBound` 同一套：
 * 最多两位小数的非负数。**允许 0** —— 「金额 ≥ 0」是一个合理的筛选意图。
 *
 * 🔴 校验用正则而不是 `Number(v)`：`Number()` 会接受 `1e3`、`0x10`、` 1 `
 * 这类后端会拒绝的输入，让用户在前端看着没问题、一提交就 400。
 */
export const AMOUNT_BOUND_RE = /^(0|[1-9]\d{0,17})(\.\d{1,2})?$/;

export function isAmountBound(raw: string): boolean {
  return AMOUNT_BOUND_RE.test(raw.trim());
}

/**
 * RangePicker 的值 → 接口参数。
 *
 * 用户选「9 月 1 日 — 9 月 30 日」时想要的是**含头含尾的整两天**，
 * 所以起点取当天 00:00:00、终点取当天 23:59:59.999。直接把 Dayjs 原样转 ISO 的话，
 * 终点会停在当天 00:00，整个 9 月 30 日的记录一条都筛不出来。
 */
export function dateRangeParams(
  range: [Dayjs | null, Dayjs | null] | null | undefined,
): { createdFrom?: string; createdTo?: string } {
  const from = range?.[0];
  const to = range?.[1];
  return {
    createdFrom: from ? from.startOf('day').toISOString() : undefined,
    createdTo: to ? to.endOf('day').toISOString() : undefined,
  };
}

/**
 * 成对出现、但在界面上只是**一个**控件的筛选项。
 * 数「生效了几个条件」时要合并，否则选一个时间区间会被数成 2 个。
 */
const PAIRED: Record<string, string> = {
  createdFrom: 'created',
  createdTo: 'created',
  amountMin: 'amount',
  amountMax: 'amount',
};

/** 当前生效的筛选条件个数。空串、空数组、undefined 都不算。 */
export function activeFilterCount(filter: object): number {
  const groups = new Set<string>();
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    groups.add(PAIRED[key] ?? key);
  }
  return groups.size;
}
